/**
 * WHERE STRIPE SENDS A PAYING LEARNER BACK TO — and until now, nowhere at all.
 *
 * `openedx_thinlms_payments/checkout.py` builds the Session's `success_url` from
 * `_success_path()`, which is `/purchase/complete?order={order_id}`, and its `cancel_url` from
 * `_cancel_path()`, `/purchase/cancelled?order={order_id}`. Neither route existed in this app.
 * `grep -rn purchase apps/web/src` found comments and nothing else. So the LAST step of every
 * successful purchase was a URL the SPA answered with its shell and no content: the learner
 * paid, was redirected, and landed on a blank page with no confirmation, no course link, and no
 * way to tell a completed payment from a failed one. Proven live against a real RM 21.80 test
 * purchase before this file existed.
 *
 * FIVE OUTCOMES, AND NOT ONE OF THEM IS "SOMETHING WENT WRONG"
 * -----------------------------------------------------------
 *   paid + enrolled      it worked. Link straight into the course.
 *   paid + not yet       the money is taken and access is being written. THIS IS NOT A FAILURE
 *                        and must not read as one: the fulfilment outbox is claiming the job.
 *                        The page keeps asking, says what it is waiting for, and — the part
 *                        that matters — promises the access is theirs either way.
 *   not paid             cancelled, expired, or they closed Stripe's page. NOTHING was charged,
 *                        said in those words, because the commonest fear at this moment is
 *                        having been charged twice.
 *   refunded / disputed  a settled state an admin or a bank created. Named, not guessed at.
 *   unknown order        a link for an order this learner does not have. NOT reported as a
 *                        failed payment — it is a page that cannot find the thing, and it says
 *                        so, because the server answers 404 identically for a foreign order id
 *                        and a nonexistent one and this page must not turn either into a claim
 *                        about somebody's money.
 *
 * IT CLAIMS NOTHING IT CANNOT SEE. The success heading needs `outcome === 'paid'` from the
 * server; the "you can start" link needs `access_granted` as well. Neither is inferred from the
 * fact that Stripe redirected here — Stripe redirects to `success_url` when its own page
 * completed, which is a statement about the payment page and not about the enrolment.
 *
 * IT NEVER FULFILS, EITHER. Polling is a read. Enrolling from a page that anybody with a URL
 * can load would put an enrolment write behind an unauthenticated Stripe redirect and race two
 * open tabs into the outbox's claim.
 *
 * THE REFERENCE IS ON EVERY STATE, AND IT USED TO DISAPPEAR ON THE ONE THAT MATTERS MOST
 * --------------------------------------------------------------------------------------
 * `Fulfilling` printed it, `AwaitingPayment` printed it, `Refunded`, `Disputed` and `Revoked`
 * printed it — and `Ready` did not. So the sequence a paying learner actually saw was: the
 * reference on screen while access was being written, then the webhook lands, the poll flips
 * the page to `Ready`, and the reference VANISHES at the exact moment the purchase becomes a
 * thing they might have a question about. A learner with a payment question had nothing to
 * quote and support had nothing to search.
 *
 * The two states with no order at all were worse than that: `Problem` and the loading spinner
 * printed nothing, and those are precisely the states where the learner most needs the
 * reference, because the page cannot tell them anything else. The id is in the URL in both
 * cases — the whole point of `?order=` — so it can always be shown.
 *
 * `OrderReference` is therefore rendered by all nine states, from one of two sources, and it
 * says which: the server's `order_id` when there is an answer, and the query string's when
 * there is not. It is also COPYABLE, because a reference a learner has to retype into a
 * support chat is a reference that arrives with a typo.
 *
 * THREE ABSENCES, KEPT APART. No `?order=` at all renders NO reference block — there is
 * genuinely nothing to show, and `Problem kind="no-order"` says so in words. A reference we
 * have but cannot verify is shown and labelled as coming from the link. `undefined` is never
 * rendered anywhere: every path into `OrderReference` passes a string.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Icon, Spinner } from '../../shared/ui';
import { ApiError } from '../../shared/lib/openedx';
import {
  formatMoney,
  loadOrderStatus,
  orderStatusKey,
  type OrderStatus,
} from '../../shared/lib/pricing';
import { courseHref } from '../../shared/lib/catalogue';
import './unit.css';

/**
 * How often the page re-asks while fulfilment is outstanding, and for how long.
 *
 * THREE SECONDS, and it stops once the answer is settled. Measured against what it is waiting
 * for. Fulfilment is `cronjob-payments-fulfil.yaml`: one Django boot every five minutes — the
 * LMS boot measured 30s on this image, which is why it is not once a minute — and then the
 * outbox is drained every 5s for 240 of those 300 seconds. So the realistic wait is about five
 * seconds, four times in five, and about a minute when the payment lands in the dead window
 * between runs. Three seconds is frequent enough that the page turns over promptly when the
 * job lands and cheap enough that a learner leaving the tab open costs one indexed row read
 * per tick.
 *
 * IT GIVES UP AFTER FOUR MINUTES rather than polling for ever — comfortably past the ~60s worst
 * case, so reaching the ceiling means something is wrong rather than slow. And "gives up" means
 * it stops ASKING and says what it knows, never that it reports a failure. Past that point the
 * honest message is "your payment is recorded and access is still being set up", which is true,
 * and a reload asks again.
 */
const POLL_MS = 3_000;
const POLL_CEILING_MS = 4 * 60_000;

export default function PurchaseComplete({ cancelled = false }: { cancelled?: boolean }) {
  const [search] = useSearchParams();
  const orderId = search.get('order') ?? '';
  const [startedAt] = useState(() => Date.now());

  const status = useQuery({
    queryKey: orderStatusKey(orderId),
    queryFn: () => loadOrderStatus(orderId),
    enabled: Boolean(orderId),
    retry: false,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      // Settled: paid AND access written, or any outcome that will not move on its own.
      if (data.outcome !== 'paid' && data.outcome !== 'awaiting_payment') return false;
      if (data.access_granted || data.access_revoked) return false;
      if (Date.now() - startedAt > POLL_CEILING_MS) return false;
      return POLL_MS;
    },
  });

  // The order id is not a heading, and no CLAIM on this page comes from the query string —
  // every outcome, amount and access decision is the server's answer.
  //
  // THIS COMMENT USED TO SAY "nothing is rendered from the query string itself", and that rule
  // is what left the loading and error states with no reference at all. The id in the URL is
  // not a claim about a payment; it is the string the learner needs in order to ask about one,
  // and `OrderReference` labels it "from this link" precisely so it cannot be mistaken for
  // something the academy confirmed.
  useEffect(() => {
    document.title = cancelled ? 'Payment cancelled' : 'Purchase';
  }, [cancelled]);

  if (!orderId) return <Problem kind="no-order" reference="" />;

  if (status.isPending) {
    return (
      <main className="success-wrap">
        <Spinner label="Checking your purchase…" />
        {/* SHOWN WHILE LOADING, not only afterwards. A learner whose page never resolves is
            the learner most likely to need to quote something, and the id is already in the
            URL — waiting for a server answer before showing it withholds it exactly when it
            is needed. Unverified, and labelled so. */}
        <OrderReference reference={orderId} verified={false} />
      </main>
    );
  }

  if (status.isError) {
    // A 404 is "we cannot find this order for you", which the server answers identically for a
    // foreign order id and one that never existed. Anything else is the academy not answering.
    const notFound = status.error instanceof ApiError && status.error.status === 404;
    return <Problem kind={notFound ? 'unknown-order' : 'unreachable'} reference={orderId} />;
  }

  const order = status.data;

  if (order.outcome === 'not_paid' || (cancelled && order.outcome === 'awaiting_payment')) {
    return <NotPaid order={order} />;
  }
  if (order.outcome === 'awaiting_payment') return <AwaitingPayment order={order} />;
  if (order.outcome === 'refunded') return <Refunded order={order} />;
  if (order.outcome === 'disputed') return <Disputed order={order} />;

  // `paid` and `no_charge` both mean the learner has bought the thing; only the sentence about
  // money differs, and `no_charge` must not claim a payment that never happened.
  if (order.access_revoked) return <Revoked order={order} />;
  if (order.access_granted) return <Ready order={order} />;
  return <Fulfilling order={order} polling={status.isFetching} />;
}

/* ------------------------------------------------------------------ helpers ----- */

function itemLink(order: OrderStatus): string {
  return order.item_kind === 'pathway'
    ? `/paths/${encodeURIComponent(order.item_key)}`
    : courseHref(order.item_key);
}

function startLink(order: OrderStatus): string {
  // A pathway has no player of its own — a learner starts one of its courses — so the path's
  // own page is where "start" leads. A course goes straight into the courseware, which resolves
  // to the first unit on a fresh enrolment and to the resume point later.
  return order.item_kind === 'pathway'
    ? `/paths/${encodeURIComponent(order.item_key)}`
    : `/learn/${encodeURIComponent(order.item_key)}`;
}

/**
 * What this learner actually paid, and what to say before anything has settled.
 *
 * THE QUOTE IS NOT THE PAYMENT, AND PRINTING IT AS ONE WAS A BUG THIS PAGE SHIPPED. Measured on
 * dev: a learner redeemed a 20% code, paid RM 84.00, and this page told them "RM 105.00 paid" —
 * overstating their own payment by exactly the discount they had just earned. `amount_minor` is
 * the number the order row was created with; `charged_minor` is what Stripe took.
 *
 * `charged_minor` is NULL until something settles, and null is not zero. While it is null this
 * page is showing a payment still in flight, so it says what was ASKED for and labels it that
 * way — never a settled-sounding "paid" against a number nobody has been charged.
 */
export type Charge =
  | { state: 'settled'; text: string; discountText: string | null }
  | { state: 'pending'; text: string }
  | { state: 'unknown' };

// EXPORTED FOR ITS TEST, and only for that. The rendering below is a formatting detail; the
// decision -- quote or charge, settled or pending -- is the part that was wrong and the part
// worth pinning, so it is reachable without mounting a router and a query client.
export function charge(order: OrderStatus): Charge {
  const currency = order.charge_currency || order.currency;
  if (!currency) return { state: 'unknown' };

  if (order.charged_minor !== null && order.charged_minor !== undefined) {
    return {
      state: 'settled',
      text: formatMoney(order.charged_minor, currency),
      // Shown only when Stripe reported one, so an ordinary purchase gains no extra line.
      discountText:
        order.discount_minor && order.discount_minor > 0
          ? formatMoney(order.discount_minor, currency)
          : null,
    };
  }
  if (!order.amount_minor) return { state: 'unknown' };
  return { state: 'pending', text: formatMoney(order.amount_minor, order.currency || currency) };
}

/**
 * The receipt line, and the ONE place this page prints money.
 *
 * The fee is named as "payment processing" and nothing else — the markup covers the ACADEMY's
 * Stripe charge and settles to the academy; Mereka takes no commission and receives none of it.
 * "Platform fee", "service fee" and "commission" would all be factually wrong here.
 */
function Receipt({ order }: { order: OrderStatus }) {
  const paid = charge(order);
  if (paid.state === 'unknown') return null;

  const currency = order.charge_currency || order.currency;
  const processing =
    order.fee_minor > 0 && currency ? formatMoney(order.fee_minor, currency) : null;

  if (paid.state === 'pending') {
    return (
      <p className="success-card__meta">
        {paid.text} to pay
        {processing ? <> · includes {processing} payment processing</> : null}
      </p>
    );
  }

  return (
    <p className="success-card__meta">
      {paid.text} paid
      {paid.discountText ? <> · {paid.discountText} discount applied</> : null}
      {processing ? <> · includes {processing} payment processing</> : null}
    </p>
  );
}

/**
 * THE ORDER REFERENCE, ON EVERY STATE, AND COPYABLE.
 *
 * One component rather than the six near-identical `<p>Reference: <code>…</code></p>` blocks
 * this file used to carry, because six copies is how the seventh state gets written without
 * one — which is exactly what happened to `Ready`.
 *
 * `verified` is NOT cosmetic. `true` means the server answered about this order, so the
 * reference is one the academy can look up. `false` means it came off the query string and
 * nothing has confirmed it — the loading state and both `Problem` states — and saying "from
 * this link" is the honest form of that, because the learner may be holding somebody else's
 * link or a mistyped one and must not be told the academy has their order.
 *
 * AN EMPTY REFERENCE RENDERS NOTHING. Absent is not the same as unknown: with no `?order=` at
 * all there is no string to show, and a block reading "Reference:" followed by nothing would
 * be worse than no block. `Problem kind="no-order"` carries that case in prose instead.
 *
 * THE COPY FALLBACK IS NOT DEFENSIVE PADDING. `navigator.clipboard` is unavailable outside a
 * secure context and rejects when the document is not focused, and this page is reached by a
 * cross-site redirect from Stripe — so the failure is a real one on a real path, and a
 * `window.prompt` the learner can select from is a working reference rather than a dead
 * button. Same shape as `CertificateView.copyLink`.
 */
function OrderReference({
  reference,
  verified = true,
}: {
  reference: string;
  verified?: boolean;
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(false), 2_500);
    return () => window.clearTimeout(timer);
  }, [copied]);

  if (!reference) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(reference);
      setCopied(true);
    } catch {
      setCopied(false);
      window.prompt('Copy this reference', reference);
    }
  };

  return (
    <p className="success-card__meta success-ref">
      <span>
        {verified ? 'Reference:' : 'Reference from this link:'}{' '}
        <code className="success-ref__code">{reference}</code>
      </span>
      <button type="button" className="ui-btn" data-variant="ghost" onClick={copy}>
        <Icon name="content_copy" />
        {copied ? 'Copied' : 'Copy'}
      </button>
      {/* The label change is announced rather than only shown, because a screen-reader user
          gets no confirmation from a button whose text they are not looking at. */}
      <span className="success-ref__status" role="status" aria-live="polite">
        {copied ? 'Reference copied to the clipboard' : ''}
      </span>
    </p>
  );
}

/* --------------------------------------------------------------- the outcomes -- */

function Ready({ order }: { order: OrderStatus }) {
  return (
    <main className="success-wrap">
      <div className="success-check" aria-hidden="true">
        <Icon name="check" />
      </div>
      <h1>You&rsquo;re in 🎉</h1>
      <p>
        {order.outcome === 'no_charge'
          ? 'Your place is confirmed and nothing was charged.'
          : 'Your payment went through and your place is confirmed.'}{' '}
        {order.item_kind === 'pathway'
          ? 'The whole learning path is yours, and so is every course in it.'
          : 'The course is on your dashboard from now on.'}
      </p>
      <Receipt order={order} />

      <div className="success-actions">
        <Link to={startLink(order)} className="ui-btn" data-variant="primary">
          <Icon name="play_arrow" />
          {order.item_kind === 'pathway' ? 'Open the learning path' : 'Start learning'}
        </Link>
        <Link to="/dashboard" className="ui-btn" data-variant="secondary">
          <Icon name="school" />
          Go to my dashboard
        </Link>
      </div>

      {/* THE STATE THAT USED TO PRINT NO REFERENCE, and the one that most needed to. This is
          the settled state, so it is what a learner is looking at when they decide to ask a
          question about the charge on their statement. */}
      <OrderReference reference={order.order_id} />
    </main>
  );
}

/**
 * PAID, AND ACCESS NOT WRITTEN YET. The state this page exists to get right.
 *
 * On dev this was the state a real purchase sat in indefinitely — `payment_state=paid`,
 * `access_state=none`, `fulfilled_at=None`, no grants — because nothing invoked the fulfilment
 * outbox. Money taken, no course, and no page to say either. So this screen makes two promises
 * and keeps them separable: the payment IS recorded (a fact, from the server), and the access is
 * coming (also a fact — the order is in the outbox and the scheduled worker claims it).
 *
 * It never says "failed", never says "try again", and never offers to pay again. A second
 * purchase is the one action that would actually cost this learner money for nothing.
 */
function Fulfilling({ order, polling }: { order: OrderStatus; polling: boolean }) {
  return (
    <main className="success-wrap">
      <div className="success-check" aria-hidden="true">
        <Icon name="hourglass_top" />
      </div>
      <h1>Payment received — setting up your access</h1>
      <p>
        {order.outcome === 'no_charge'
          ? 'Your place is confirmed and we are just setting up your access.'
          : 'We have your payment. Your access is being set up now, and it is yours whether or not you stay on this page.'}
      </p>
      <Receipt order={order} />

      <p className="success-card__meta">
        {polling ? (
          <>
            <Spinner label="Checking again…" /> This usually takes under a minute.
          </>
        ) : (
          <>
            This usually takes under a minute. Reload this page to check again, or open your
            dashboard — the course appears there as soon as it is ready.
          </>
        )}
      </p>

      <div className="success-actions">
        <Link to="/dashboard" className="ui-btn" data-variant="primary">
          <Icon name="school" />
          Go to my dashboard
        </Link>
        <Link to={itemLink(order)} className="ui-btn" data-variant="secondary">
          {order.item_kind === 'pathway' ? 'View the learning path' : 'View the course'}
        </Link>
      </div>

      <OrderReference reference={order.order_id} />
    </main>
  );
}

function AwaitingPayment({ order }: { order: OrderStatus }) {
  return (
    <main className="success-wrap">
      <div className="success-check success-check--problem" aria-hidden="true">
        <Icon name="hourglass_top" />
      </div>
      <h1>Your payment has not completed</h1>
      <p>
        Some payment methods take a few minutes to confirm — FPX and GrabPay both can. Nothing
        more is needed from you: if it goes through, your access appears automatically and you
        will not be asked to pay again. If it does not, nothing will be charged.
      </p>
      <div className="success-actions">
        <Link to={itemLink(order)} className="ui-btn" data-variant="primary">
          {order.item_kind === 'pathway' ? 'Back to the learning path' : 'Back to the course'}
        </Link>
        <Link to="/dashboard" className="ui-btn" data-variant="secondary">
          Go to my dashboard
        </Link>
      </div>
      <OrderReference reference={order.order_id} />
    </main>
  );
}

function NotPaid({ order }: { order: OrderStatus }) {
  return (
    <main className="success-wrap">
      <div className="success-check success-check--problem" aria-hidden="true">
        <Icon name="do_not_disturb_on" />
      </div>
      <h1>Payment cancelled</h1>
      <p>
        {/* The first sentence, and the reason it is first: the commonest worry at this moment is
            having been charged anyway. */}
        <strong>Nothing has been charged.</strong> You can pick up where you left off whenever
        you like — the price is unchanged.
      </p>
      <div className="success-actions">
        <Link to={itemLink(order)} className="ui-btn" data-variant="primary">
          {order.item_kind === 'pathway' ? 'Back to the learning path' : 'Back to the course'}
        </Link>
        <Link to="/discover" className="ui-btn" data-variant="secondary">
          Browse other courses
        </Link>
      </div>

      {/* Shown on a cancellation too. An order that was started and not paid is still an order
          in the academy's ledger, and "you charged me and then said it was cancelled" is a
          support conversation that needs a reference as much as any other. */}
      <OrderReference reference={order.order_id} />
    </main>
  );
}

function Refunded({ order }: { order: OrderStatus }) {
  return (
    <main className="success-wrap">
      <div className="success-check success-check--problem" aria-hidden="true">
        <Icon name="undo" />
      </div>
      <h1>This purchase has been refunded</h1>
      <p>
        The academy has refunded this order, so the access that came with it has been removed.
        Refunds usually reach your account within a few days, depending on your bank.
      </p>
      <Receipt order={order} />
      <div className="success-actions">
        <Link to={itemLink(order)} className="ui-btn" data-variant="primary">
          {order.item_kind === 'pathway' ? 'View the learning path' : 'View the course'}
        </Link>
        <Link to="/dashboard" className="ui-btn" data-variant="secondary">
          Go to my dashboard
        </Link>
      </div>
      <OrderReference reference={order.order_id} />
    </main>
  );
}

function Disputed({ order }: { order: OrderStatus }) {
  return (
    <main className="success-wrap">
      <div className="success-check success-check--problem" aria-hidden="true">
        <Icon name="gavel" />
      </div>
      <h1>This payment is being reviewed by the bank</h1>
      <p>
        A dispute has been raised on this payment, so it is with the card issuer rather than with
        the academy. Nothing more is needed from you here; the academy will be in touch if it
        needs anything.
      </p>
      <div className="success-actions">
        <Link to="/dashboard" className="ui-btn" data-variant="secondary">
          Go to my dashboard
        </Link>
      </div>
      <OrderReference reference={order.order_id} />
    </main>
  );
}

function Revoked({ order }: { order: OrderStatus }) {
  return (
    <main className="success-wrap">
      <div className="success-check success-check--problem" aria-hidden="true">
        <Icon name="lock" />
      </div>
      <h1>This access has been removed</h1>
      <p>
        The payment for this order went through, and the access it granted has since been taken
        back by the academy. If that is not what you expected, contact the academy and quote the
        reference below.
      </p>
      <Receipt order={order} />
      <div className="success-actions">
        <Link to="/dashboard" className="ui-btn" data-variant="secondary">
          Go to my dashboard
        </Link>
      </div>
      <OrderReference reference={order.order_id} />
    </main>
  );
}

/**
 * The two states where there is no order to talk about.
 *
 * NEITHER IS REPORTED AS A FAILED PAYMENT, and that distinction is the whole reason this is a
 * separate component. An unknown order id means this page cannot find the order — which is what
 * the server says when the id is not this learner's, and it must not become "your payment
 * failed", because it might be somebody else's perfectly good purchase. And an academy that did
 * not answer has told us nothing at all about the money.
 */
function Problem({
  kind,
  reference,
}: {
  kind: 'no-order' | 'unknown-order' | 'unreachable';
  reference: string;
}) {
  return (
    <main className="success-wrap">
      <div className="success-check success-check--problem" aria-hidden="true">
        <Icon name="priority_high" />
      </div>
      <h1>
        {kind === 'unreachable'
          ? 'We could not check your purchase'
          : 'We could not find this purchase'}
      </h1>
      <p>
        {kind === 'no-order'
          ? 'This page arrived without an order reference, so there is nothing to look up. Your dashboard lists everything you have access to.'
          : kind === 'unknown-order'
            ? 'This academy has no purchase with that reference for your account. If you were sent this link, it may belong to a different account or a different academy — nothing here says anything about a payment either way.'
            : 'The academy did not answer, so we cannot say what happened to this purchase. Your payment is not affected by this page — reload to try again, and your dashboard is the place that knows what you have access to.'}
      </p>
      <div className="success-actions">
        <Link to="/dashboard" className="ui-btn" data-variant="primary">
          <Icon name="school" />
          Go to my dashboard
        </Link>
        <Link to="/discover" className="ui-btn" data-variant="secondary">
          Browse courses
        </Link>
      </div>

      {/* UNVERIFIED, AND SAID SO. These are the two states where the reference is worth the
          most — the page cannot tell the learner anything else, so the reference is the only
          thing support can work from — and the two where claiming it is confirmed would be a
          lie: for `unknown-order` the academy has explicitly said it has no such order for
          this account. `no-order` passes an empty string and renders nothing. */}
      <OrderReference reference={reference} verified={false} />
    </main>
  );
}
